import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/Button';
import { getUrlParam } from '../utils/url';
import { getMyDietPlan, submitDietSurvey, DietPlan, DietCalendarSlot, getDietCalendarSlots } from '../api/dietApi';

interface DietSurveyPageProps {
  onComplete: () => void;
}

export const DietSurveyPage: React.FC<DietSurveyPageProps> = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState<DietPlan | null>(null);
  const [slots, setSlots] = useState<DietCalendarSlot[]>([]);
  
  const [formData, setFormData] = useState({
    // Krok 1 (Jadlospis/Pakiet)
    diet_allergies: '',
    diet_excluded: '',
    diet_style: '',
    diet_illnesses: '',
    // Krok 2 (Jadlospis/Pakiet)
    diet_activity: '',
    diet_meals: '',
    diet_equipment: '',
    diet_favorites: '',
    diet_notes: '',
    // Konsultacja
    patient_question: '',
    slot: ''
  });

  const orderId = getUrlParam('order_id');

  useEffect(() => {
    getMyDietPlan().then(p => {
      setPlan(p);
      if (p.order_type === 'konsultacja' || p.order_type === 'pakiet') {
        getDietCalendarSlots().then(res => setSlots(res.slots));
      }
    }).catch(() => {});
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNext = () => setStep(s => s + 1);
  const handlePrev = () => setStep(s => s - 1);

  const handleSubmit = async () => {
    if (!orderId) {
      alert('Błąd: brak ID zamówienia');
      return;
    }
    
    setLoading(true);
    try {
      await submitDietSurvey(parseInt(orderId), formData);
      onComplete();
    } catch (e) {
      alert('Wystąpił błąd podczas wysyłania ankiety.');
      setLoading(false);
    }
  };

  const type = plan?.order_type || 'jadlospis';
  
  // Визначаємо флоу:
  // jadlospis: 2 кроки (питання)
  // konsultacja: 2 кроки (patient_question, calendar)
  // pakiet: 3 кроки (питання 1, питання 2, calendar)
  let totalSteps = 2;
  if (type === 'konsultacja') totalSteps = 2;
  if (type === 'pakiet') totalSteps = 3;

  const renderCalendarStep = () => {
    const dates = Array.from(new Set(slots.map(s => s.date)));

    return (
      <div>
        <h2 style={{ fontSize: 18, color: '#361777', marginBottom: 16 }}>Wybierz termin konsultacji</h2>
        <p style={{ fontSize: 14, color: '#57437E', marginBottom: 12 }}>Wybierz dogodny dzień i godzinę na rozmowę wideo z dietetykiem.</p>
        
        {dates.length === 0 ? (
          <div style={{ padding: 20, background: '#FFF0F0', color: '#D32F2F', borderRadius: 12 }}>
            Brak dostępnych terminów. Skontaktuj się z obsługą.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {dates.map(date => (
              <div key={date}>
                <div style={{ fontWeight: 600, color: '#361777', marginBottom: 8 }}>{date}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {slots.filter(s => s.date === date).map(s => {
                    const isSelected = formData.slot === `${s.date} ${s.start_time}`;
                    return (
                      <button
                        key={s.start_time}
                        onClick={() => setFormData({ ...formData, slot: `${s.date} ${s.start_time}` })}
                        style={{
                          padding: '8px 16px',
                          borderRadius: 8,
                          border: isSelected ? '2px solid #361777' : '1px solid #E6D6FA',
                          background: isSelected ? '#FAF8FE' : '#fff',
                          color: '#361777',
                          fontWeight: isSelected ? 600 : 400,
                          cursor: 'pointer'
                        }}
                      >
                        {s.start_time}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderStepContent = () => {
    // === KONSULTACJA FLOW ===
    if (type === 'konsultacja') {
      if (step === 1) {
        return (
          <div>
            <h2 style={{ fontSize: 18, color: '#361777', marginBottom: 16 }}>Cel konsultacji</h2>
            <p style={{ fontSize: 14, color: '#57437E', marginBottom: 12 }}>O czym chciał(a)byś porozmawiać na konsultacji? (Opcjonalnie, ale pomoże dietetykowi się przygotować)</p>
            <textarea 
              name="patient_question" 
              value={formData.patient_question} 
              onChange={handleChange}
              style={{ width: '100%', height: 120, padding: 12, borderRadius: 12, border: '1px solid #E6D6FA', outline: 'none', fontFamily: 'inherit', resize: 'vertical' }}
              placeholder="Np. Chcę skonsultować wyniki badań, mam problem ze schudnięciem..."
            />
          </div>
        );
      }
      if (step === 2) return renderCalendarStep();
    }

    // === JADŁOSPIS / PAKIET FLOW ===
    if (step === 1) {
      return (
        <div>
          <h2 style={{ fontSize: 18, color: '#361777', marginBottom: 16 }}>Krok 1: Informacje o pacjencie</h2>
          
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, color: '#361777', fontWeight: 600, marginBottom: 8 }}>Alergie i nietolerancje pokarmowe</label>
            <textarea name="diet_allergies" value={formData.diet_allergies} onChange={handleChange}
              style={{ width: '100%', height: 80, padding: 12, borderRadius: 8, border: '1px solid #E6D6FA', outline: 'none', fontFamily: 'inherit' }}
              placeholder="Np. alergia na orzechy, nietolerancja laktozy..." />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, color: '#361777', fontWeight: 600, marginBottom: 8 }}>Czego nie chcesz w jadłospisie</label>
            <textarea name="diet_excluded" value={formData.diet_excluded} onChange={handleChange}
              style={{ width: '100%', height: 80, padding: 12, borderRadius: 8, border: '1px solid #E6D6FA', outline: 'none', fontFamily: 'inherit' }}
              placeholder="Np. nienawidzę ryb i brukselki..." />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, color: '#361777', fontWeight: 600, marginBottom: 8 }}>Jak się odżywiasz / styl diety</label>
            <textarea name="diet_style" value={formData.diet_style} onChange={handleChange}
              style={{ width: '100%', height: 80, padding: 12, borderRadius: 8, border: '1px solid #E6D6FA', outline: 'none', fontFamily: 'inherit' }}
              placeholder="Np. jem wszystko, wegetarianizm, jem głównie rano..." />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, color: '#361777', fontWeight: 600, marginBottom: 8 }}>Schorzenia, które mamy uwzględnić</label>
            <textarea name="diet_illnesses" value={formData.diet_illnesses} onChange={handleChange}
              style={{ width: '100%', height: 80, padding: 12, borderRadius: 8, border: '1px solid #E6D6FA', outline: 'none', fontFamily: 'inherit' }}
              placeholder="Np. insulinooporność, niedoczynność tarczycy, biorę leki..." />
          </div>
        </div>
      );
    }
    
    if (step === 2) {
      return (
        <div>
          <h2 style={{ fontSize: 18, color: '#361777', marginBottom: 16 }}>Krok 2: Preferencje</h2>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, color: '#361777', fontWeight: 600, marginBottom: 8 }}>Twoja aktywność w ciągu dnia</label>
            <select name="diet_activity" value={formData.diet_activity} onChange={handleChange}
              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #E6D6FA', outline: 'none', fontFamily: 'inherit', background: '#fff' }}>
              <option value="">Wybierz...</option>
              <option value="niska">Niska (praca siedząca, brak ćwiczeń)</option>
              <option value="umiarkowana">Umiarkowana (trochę ruchu, spacery)</option>
              <option value="wysoka">Wysoka (praca fizyczna lub regularne treningi)</option>
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, color: '#361777', fontWeight: 600, marginBottom: 8 }}>Ile posiłków / dla ilu osób gotujesz</label>
            <select name="diet_meals" value={formData.diet_meals} onChange={handleChange}
              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #E6D6FA', outline: 'none', fontFamily: 'inherit', background: '#fff' }}>
              <option value="">Wybierz...</option>
              <option value="3_posilki">3 posiłki (dla siebie)</option>
              <option value="4_posilki">4 posiłki (dla siebie)</option>
              <option value="5_posilkow">5 posiłków (dla siebie)</option>
              <option value="z_rodzina">Gotuję dla całej rodziny</option>
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, color: '#361777', fontWeight: 600, marginBottom: 8 }}>Jaki masz sprzęt kuchenny</label>
            <select name="diet_equipment" value={formData.diet_equipment} onChange={handleChange}
              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #E6D6FA', outline: 'none', fontFamily: 'inherit', background: '#fff' }}>
              <option value="">Wybierz...</option>
              <option value="podstawowy">Podstawowy (kuchenka, lodówka, patelnia)</option>
              <option value="piekarnik">Mam piekarnik</option>
              <option value="mikrofalowka">Mam mikrofalówkę</option>
              <option value="pelny">Pełny sprzęt (piekarnik, blender, mikrofala)</option>
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, color: '#361777', fontWeight: 600, marginBottom: 8 }}>Twoje ulubione produkty lub dania</label>
            <textarea name="diet_favorites" value={formData.diet_favorites} onChange={handleChange}
              style={{ width: '100%', height: 80, padding: 12, borderRadius: 8, border: '1px solid #E6D6FA', outline: 'none', fontFamily: 'inherit' }}
              placeholder="Np. uwielbiam makarony, awokado, kurczaka..." />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, color: '#361777', fontWeight: 600, marginBottom: 8 }}>Coś jeszcze...</label>
            <textarea name="diet_notes" value={formData.diet_notes} onChange={handleChange}
              style={{ width: '100%', height: 80, padding: 12, borderRadius: 8, border: '1px solid #E6D6FA', outline: 'none', fontFamily: 'inherit' }}
              placeholder="Dodatkowe uwagi dla dietetyka..." />
          </div>
        </div>
      );
    }
    
    // Pakiet has a calendar step
    if (step === 3 && type === 'pakiet') return renderCalendarStep();

    return null;
  };

  const isNextDisabled = () => {
    if (type === 'konsultacja') {
      if (step === 2 && !formData.slot) return true;
    } else if (type === 'pakiet') {
      if (step === 3 && !formData.slot) return true;
    }
    return false;
  };

  if (!plan) return <div style={{ padding: 20, textAlign: 'center' }}>Ładowanie...</div>;

  return (
    <div className="cab-card" style={{ maxWidth: 600, margin: '0 auto', background: '#fff' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div key={i} style={{ 
            flex: 1, 
            height: 6, 
            borderRadius: 3, 
            background: i + 1 <= step ? '#361777' : '#E6D6FA',
            transition: 'background 0.3s'
          }} />
        ))}
      </div>

      {renderStepContent()}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
        {step > 1 ? (
          <Button variant="outline" onClick={handlePrev}>Wstecz</Button>
        ) : <div />}
        
        {step < totalSteps ? (
          <Button onClick={handleNext} disabled={isNextDisabled()}>Dalej</Button>
        ) : (
          <Button onClick={handleSubmit} loading={loading} disabled={isNextDisabled()}>Wyślij ankietę</Button>
        )}
      </div>
    </div>
  );
};
